/**
 * /affiliate/login — WhatsApp OTP-only sign-in for affiliates.
 *
 * The affiliate portal deliberately refuses email/password. Identity is
 * verified via the existing hardened WhatsApp OTP pipeline
 * (whatsappRequestSignupOtp / whatsappVerifySignupOtp), then Supabase's
 * `verifyOtp` completes the session. Post-session we call
 * `bindMyAffiliateAccount` — which is the one-user/one-affiliate function —
 * and route to /affiliate/status regardless of the bind outcome (the status
 * router handles every subsequent state; the bind response never leaks
 * whether a matching phone exists).
 *
 * All negative outcomes collapse to enumeration-safe messages: no
 * "no such affiliate" copy, no "wrong phone" hints.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  whatsappRequestSignupOtp,
  whatsappVerifySignupOtp,
} from "@/lib/whatsapp.functions";
import { bindMyAffiliateAccount } from "@/lib/affiliate/identity.functions";
import {
  whatsappRequestErrorCopy,
  whatsappVerifyErrorCopy,
} from "@/lib/whatsapp/error-messages";

export const Route = createFileRoute("/affiliate/login")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Affiliate sign in · TruWe" },
      {
        name: "description",
        content: "Sign in to the TruWe Affiliate portal with WhatsApp OTP.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AffiliateLoginPage,
});

function normalizePhoneClient(input: string): string | null {
  const digits = input.replace(/[^\d]/g, "");
  if (!digits) return null;
  let d = digits;
  if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  if (d.length === 10) return `+91${d}`;
  return null;
}

function AffiliateLoginPage() {
  const navigate = useNavigate();
  const requestOtp = useServerFn(whatsappRequestSignupOtp);
  const verifyOtpFn = useServerFn(whatsappVerifySignupOtp);
  const bindFn = useServerFn(bindMyAffiliateAccount);

  const [method, setMethod] = useState<"whatsapp" | "email">("whatsapp");
  const [stage, setStage] = useState<"phone" | "code">("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef<number | null>(null);

  async function afterSession() {
    try {
      await bindFn();
    } catch {
      /* generic — status router handles */
    }
    navigate({ to: "/affiliate/status", replace: true });
  }

  // Redirect immediately if a session already exists — the status router
  // handles every downstream case without leaking anything about the account.
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) navigate({ to: "/affiliate/status", replace: true });
    });
  }, [navigate]);


  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = window.setTimeout(
      () => setCooldown((s) => Math.max(0, s - 1)),
      1000,
    );
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [cooldown]);

  async function sendCode(isResend = false) {
    if (busy || cooldown > 0) return;
    const normalized = normalizePhoneClient(phone);
    if (!normalized) {
      toast.error("Enter a valid 10-digit Indian mobile number");
      return;
    }
    setBusy(true);
    try {
      const r = await requestOtp({ data: { phone: normalized } });
      if (r.ok) {
        toast.success(isResend ? "New code sent on WhatsApp" : "Code sent on WhatsApp");
        setStage("code");
        setCode("");
        setCooldown(r.cooldownSeconds ?? 45);
      } else {
        const retry = "retryAfterSec" in r ? r.retryAfterSec : undefined;
        const copy = whatsappRequestErrorCopy(r.error, retry);
        toast.error(copy.title, { description: copy.hint });
        if (r.error === "rate_limited") setCooldown(retry ?? 60);
      }
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    if (busy) return;
    const normalized = normalizePhoneClient(phone);
    if (!normalized || code.length < 4) {
      toast.error("Enter the 6-digit code", {
        description: "Check your WhatsApp chat for the latest code we sent.",
      });
      return;
    }
    setBusy(true);
    try {
      const r = await verifyOtpFn({ data: { phone: normalized, code } });
      if (!r.ok) {
        // On the affiliate portal, email_required means the number isn't
        // linked to any TruWe account yet — nudge to the main sign-up.
        if (r.error === "email_required") {
          toast.error("No TruWe account for this number", {
            description: "Sign up on the main site first, then come back to log in as an affiliate.",
          });
        } else {
          const copy = whatsappVerifyErrorCopy(r.error);
          toast.error(copy.title, { description: copy.hint });
        }
        if (
          r.error === "expired" ||
          r.error === "too_many_attempts" ||
          r.error === "no_active_code"
        ) {
          setCode("");
          setCooldown(0);
        }
        return;
      }
      // Establish the Supabase session (magiclink hashed token).
      const { error } = await supabase.auth.verifyOtp({
        token_hash: r.hashedToken,
        type: "magiclink",
      });
      if (error) {
        const copy = whatsappVerifyErrorCopy("session_error");
        toast.error(copy.title, { description: copy.hint });
        return;
      }

      await afterSession();
    } finally {
      setBusy(false);
    }
  }

  async function signInWithEmail(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!email.trim() || password.length < 6) {
      toast.error("Enter your email and password");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) {
        // Enumeration-safe generic message.
        toast.error("Sign-in failed. Check your details and try again.");
        return;
      }
      await afterSession();
    } finally {
      setBusy(false);
    }
  }


  return (
    <main className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-14">
      <h1 className="font-display text-2xl font-semibold text-brand-deep">
        Affiliate sign in
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {method === "whatsapp"
          ? "Use the WhatsApp number you registered with. We'll send a one-time code."
          : "Sign in with the email and password on your affiliate account."}
      </p>

      <div className="my-6 grid grid-cols-2 gap-2 rounded-lg border border-border p-1">
        <button
          type="button"
          onClick={() => {
            setMethod("whatsapp");
            setStage("phone");
            setCode("");
          }}
          className={`rounded-md py-2 text-sm font-medium transition ${method === "whatsapp" ? "bg-brand-deep text-white" : "text-muted-foreground hover:bg-muted"}`}
        >
          WhatsApp OTP
        </button>
        <button
          type="button"
          onClick={() => setMethod("email")}
          className={`rounded-md py-2 text-sm font-medium transition ${method === "email" ? "bg-brand-deep text-white" : "text-muted-foreground hover:bg-muted"}`}
        >
          Email &amp; password
        </button>
      </div>

      {method === "email" && (
        <form onSubmit={signInWithEmail} className="space-y-4">
          <div>
            <Label htmlFor="aff-email">Email</Label>
            <Input
              id="aff-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="aff-password">Password</Label>
            <Input
              id="aff-password"
              type="password"
              autoComplete="current-password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={busy} className="w-full">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      )}

      {method === "whatsapp" && stage === "phone" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void sendCode(false);
          }}
          className="space-y-4"
          aria-labelledby="aff-login-heading"
        >
          <div>
            <Label htmlFor="aff-phone">WhatsApp mobile number</Label>
            <Input
              id="aff-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="10-digit Indian mobile"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              aria-required="true"
            />
          </div>
          <Button type="submit" disabled={busy} className="w-full">
            {busy ? "Sending…" : "Send code on WhatsApp"}
          </Button>
        </form>
      )}

      {method === "whatsapp" && stage === "code" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void verify();
          }}
          className="space-y-4"
        >
          <p className="text-sm text-muted-foreground">
            Enter the 6-digit code sent to <strong>{phone}</strong>.
          </p>
          <div>
            <Label htmlFor="aff-code">Verification code</Label>
            <Input
              id="aff-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, ""))}
              required
              aria-required="true"
            />
          </div>
          <Button type="submit" disabled={busy || code.length < 4} className="w-full">
            {busy ? "Verifying…" : "Sign in"}
          </Button>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <button
              type="button"
              onClick={() => setStage("phone")}
              className="hover:text-brand-deep"
            >
              ← Change number
            </button>
            <button
              type="button"
              onClick={() => void sendCode(true)}
              disabled={cooldown > 0 || busy}
              className="hover:text-brand-deep disabled:opacity-50"
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
            </button>
          </div>
        </form>
      )}


      <div className="mt-10 text-xs text-muted-foreground">
        <p>
          Not an affiliate yet?{" "}
          <Link to="/affiliate/apply" className="text-brand-deep underline">
            Apply here
          </Link>
          .
        </p>
        <p className="mt-1">
          By continuing you accept the{" "}
          <Link to="/policies/affiliate-terms" className="underline">
            Affiliate Partner Agreement
          </Link>{" "}
          and{" "}
          <Link to="/policies/privacy-policy" className="underline">
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </main>
  );
}
